import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "@/components/Toast";
import { ArrowLeft, Lock, Calculator, Sliders, History, Send } from "lucide-react";
import { SimulateTab } from "./SimulateTab";
import { SettingsTab } from "./SettingsTab";
import { ClientTab } from "./ClientTab";
import { HistoryTab } from "./HistoryTab";

// Pestaña "Presupuesto" — solo admin, protegida además por un PIN configurable
// (segundo factor simple, distinto de la contraseña de la cuenta) para que no
// lo vea cualquiera que pase por la pantalla.
export default function Budget() {
  const [status, setStatus] = useState(null); // { configured }
  const [token, setToken] = useState(null);
  const [tab, setTab] = useState("simulate");

  const loadStatus = async () => {
    try { const { data } = await api.get("/budget/pin/status"); setStatus(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { loadStatus(); }, []);

  return (
    <div style={{ padding: "20px 32px", maxWidth: 1200, margin: "0 auto" }} data-testid="budget-page">
      <Link to="/admin" style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "var(--invict-text-muted)", fontSize: 12, marginBottom: 12, textDecoration: "none", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}><ArrowLeft size={14}/> Volver</Link>
      <h1 style={{ fontSize: 26, fontWeight: 900, margin: "4px 0 4px", color: "#0A0A0A", letterSpacing: "-0.02em" }}>Presupuesto</h1>
      <p style={{ fontSize: 12, color: "var(--invict-text-muted)", marginBottom: 18 }}>Simulá el costo y precio de un servicio a partir de sus franjas horarias contratadas. Acceso protegido por PIN.</p>

      {!status && <div className="skel" style={{ height: 120 }} />}

      {status && !status.configured && (
        <PinSetup onDone={(t) => { setToken(t); setStatus({ configured: true }); }} />
      )}

      {status && status.configured && !token && (
        <PinVerify onVerified={(t) => setToken(t)} />
      )}

      {status && status.configured && token && (
        <>
          <div style={{ display: "flex", gap: 8, marginBottom: 18, flexWrap: "wrap" }}>
            <TabBtn active={tab === "simulate"} onClick={() => setTab("simulate")} Ic={Calculator} label="Simular presupuesto" testid="budget-tab-simulate"/>
            <TabBtn active={tab === "settings"} onClick={() => setTab("settings")} Ic={Sliders} label="Parámetros" testid="budget-tab-settings"/>
            <TabBtn active={tab === "client"} onClick={() => setTab("client")} Ic={Send} label="Cliente" testid="budget-tab-client"/>
            <TabBtn active={tab === "history"} onClick={() => setTab("history")} Ic={History} label="Historial" testid="budget-tab-history"/>
          </div>
          {tab === "simulate" && <SimulateTab token={token} />}
          {tab === "settings" && <SettingsTab token={token} />}
          {tab === "client" && <ClientTab token={token} />}
          {tab === "history" && <HistoryTab token={token} />}
        </>
      )}
    </div>
  );
}

function TabBtn({ active, onClick, Ic, label, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "11px 18px", borderRadius: 8,
        border: "none", background: active ? "#0A0A0A" : "#fff", color: active ? "#FFFBF4" : "#0A0A0A",
        fontWeight: 800, fontSize: 13, cursor: "pointer", boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }}>
      <Ic size={16}/> {label}
    </button>
  );
}

function PinSetup({ onDone }) {
  const [pin, setPin] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pin.length < 4) return toast.error("El PIN debe tener al menos 4 dígitos");
    if (pin !== confirm) return toast.error("Los PIN no coinciden");
    setBusy(true);
    try {
      const { data } = await api.post("/budget/pin/setup", { pin });
      toast.success("PIN configurado");
      onDone(data.token);
    } catch (e2) { toast.error(formatApiError(e2)); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="card" style={{ padding: 24, maxWidth: 380 }} data-testid="budget-pin-setup-form">
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <Lock size={18} color="#BF3437"/><h3 style={{ margin: 0, fontSize: 15 }}>Configurá un PIN de acceso</h3>
      </div>
      <p style={{ fontSize: 12, color: "var(--invict-text-muted)", margin: "0 0 14px" }}>Es la primera vez que se accede a Presupuesto. Definí un PIN numérico (4 a 8 dígitos) distinto de tu contraseña.</p>
      <div className="field"><label>PIN</label>
        <input type="password" inputMode="numeric" maxLength={8} value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))} data-testid="budget-pin-input"/>
      </div>
      <div className="field"><label>Confirmar PIN</label>
        <input type="password" inputMode="numeric" maxLength={8} value={confirm} onChange={(e) => setConfirm(e.target.value.replace(/\D/g, ""))} data-testid="budget-pin-confirm-input"/>
      </div>
      <button type="submit" disabled={busy} className="btn-primary" style={{ width: "100%", padding: 12 }} data-testid="budget-pin-setup-submit">{busy ? "Guardando..." : "Configurar PIN"}</button>
    </form>
  );
}

function PinVerify({ onVerified }) {
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await api.post("/budget/pin/verify", { pin });
      onVerified(data.token);
    } catch (e2) { toast.error(formatApiError(e2)); setPin(""); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="card" style={{ padding: 24, maxWidth: 340 }} data-testid="budget-pin-verify-form">
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <Lock size={18} color="#BF3437"/><h3 style={{ margin: 0, fontSize: 15 }}>Ingresá el PIN</h3>
      </div>
      <div className="field"><label>PIN de acceso</label>
        <input autoFocus type="password" inputMode="numeric" maxLength={8} value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))} data-testid="budget-pin-verify-input"/>
      </div>
      <button type="submit" disabled={busy || !pin} className="btn-primary" style={{ width: "100%", padding: 12 }} data-testid="budget-pin-verify-submit">{busy ? "Verificando..." : "Ingresar"}</button>
    </form>
  );
}
